import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function grow(scale: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	scale = scale ? scale : 1.1;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 			: AUTO_STYLE,
				transform 			: `scale3d(${scale}, ${scale}, ${scale})`,
				transitionProperty 	: 'transform',
				easing 				: 'ease',
				offset 				: 0
			}),
			style({
				transform 			: 'scale3d(1, 1, 1)',
				transitionProperty 	: 'transform',
				easing 				: 'ease',
				offset 				: 1
			})
		]),
		end		: keyframes([
			style({
				visibility 			: AUTO_STYLE,
				transform 			: 'scale3d(1, 1, 1)',
				transitionProperty 	: 'transform',
				easing 				: 'ease',
				offset 				: 0
			}),
			style({
				transform 			: `scale3d(${scale}, ${scale}, ${scale})`,
				transitionProperty 	: 'transform',
				easing 				: 'ease',
				offset 				: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Grow on Event
 */
@Directive({
    selector: '[ngGrowOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgGrowOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.effectOptions?.scale && this.effectOptions?.scale < 0) throw new Error(`GrowOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.effectOptions?.scale}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: grow(this.effectOptions?.scale),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Grow on Mouse Hover
 */
@Directive({
    selector: '[ngGrowOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgGrowOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.timingOptions?.scale && this.timingOptions?.scale < 0) throw new Error(`GrowOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.timingOptions.scale}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: grow(this.timingOptions?.scale),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	scale : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	scale : number;
}
